import { useTranslation } from 'react-i18next';
import { Icon } from '../common/Icon';
import { MaterialPageLink } from './MaterialPageLink';
import { DifficultyMeter, TagChips, TaskTypeBadge } from './TaskBadges';
import { TaskViewProps } from './taskComponentProps';

/**
 * A task read-only, the way a player meets it (QZ-D41, QZ-D47): the answers for each of its
 * types on the left, its details, source and explanation on the right. The question, status
 * and rejection reason are shown by `TaskPage`, not here.
 */
export const TaskView = ({ task }: TaskViewProps) => {
  const { t } = useTranslation();
  const original = task.translations[task.originalLanguage];
  const { url, document, section, page, kind } = task.sourceReference ?? {};

  return (
    <div className="row g-4">
      <div className="col-lg-8">
        <div className="qz-card">
          <div className="qz-card-body qz-stack">
            {original?.choices && (
              <section>
                <h2 className="qz-section-title">{t('task.type.MultiChoiceQuestion')}</h2>
                <ul className="qz-answers">
                  {original.choices.map((choice, index) =>
                    index === 0 ? (
                      <li key={index} className="is-correct">
                        <Icon name="circle-check" />
                        <span className="visually-hidden">
                          {t('task.field.correctChoice')}
                        </span>{' '}
                        {choice}
                      </li>
                    ) : (
                      <li key={index}>
                        <span className="qz-answer-marker" aria-hidden="true">
                          {String.fromCharCode(65 + index)}
                        </span>
                        {choice}
                      </li>
                    )
                  )}
                </ul>
              </section>
            )}

            {original?.freetextAnswer !== undefined && (
              <section>
                <h2 className="qz-section-title">{t('task.type.FreetextQuestion')}</h2>
                <ul className="qz-answers">
                  <li className="is-correct">
                    <Icon name="circle-check" />
                    {original.freetextAnswer}
                  </li>
                </ul>
              </section>
            )}

            {task.numericAnswer !== undefined && (
              <section>
                <h2 className="qz-section-title">{t('task.type.NumericTiebreakerQuestion')}</h2>
                <div className="qz-big-number">{task.numericAnswer}</div>
              </section>
            )}

            {original?.items && (
              <section>
                <h2 className="qz-section-title">{t('task.type.ItemOrdererer')}</h2>
                <ol className="qz-answers is-ordered">
                  {original.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ol>
              </section>
            )}

            {task.taskTypes.includes('TiebreakerQuestion') && (
              <section>
                <h2 className="qz-section-title">{t('task.type.TiebreakerQuestion')}</h2>
                <p className="qz-small qz-muted mb-0">{t('task.view.tiebreaker')}</p>
              </section>
            )}
          </div>
        </div>

        <div className="qz-card">
          <div className="qz-card-header">
            <h3>{t('task.field.explanation')}</h3>
            <span className="qz-small qz-muted">{t('task.form.forReviewers')}</span>
          </div>
          <div className="qz-card-body">{task.explanation}</div>
        </div>
      </div>

      <div className="col-lg-4">
        <div className="qz-card">
          <div className="qz-card-body">
            <dl className="qz-kv">
              <dt>{t('task.field.taskTypes')}</dt>
              <dd>
                <div className="d-flex flex-wrap gap-1 justify-content-end">
                  {task.taskTypes.map((type) => (
                    <TaskTypeBadge key={type} type={type} />
                  ))}
                </div>
              </dd>
              <dt>{t('task.field.difficulty')}</dt>
              <dd>
                <DifficultyMeter difficulty={task.difficulty} />
              </dd>
              <dt>{t('task.field.tags')}</dt>
              <dd className="d-flex justify-content-end">
                <TagChips tags={task.tags} />
              </dd>
              {task.languageLimit && (
                <>
                  <dt>{t('task.field.languageLimit')}</dt>
                  <dd>
                    {task.languageLimit
                      .map((language) => t(`task.language.${language}`))
                      .join(', ')}
                  </dd>
                </>
              )}
            </dl>
          </div>
        </div>

        <div className="qz-card">
          <div className="qz-card-header">
            <h3>{t('task.form.source')}</h3>
          </div>
          <div className="qz-card-body qz-small">
            {!url && !document && <span className="qz-muted">{t('task.source.none')}</span>}
            {url && (
              <a href={url} target="_blank" rel="noreferrer" className="text-break">
                {url}
              </a>
            )}
            {document && (
              <div className={url ? 'mt-2' : undefined}>
                <Icon name={kind === 'photos' ? 'images' : 'file-pdf'} /> {document}
                {section && ` · ${section}`}
                {page !== undefined && (
                  <>
                    {' · '}
                    <MaterialPageLink jobId={task.jobId} page={page} kind={kind} />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskView;
